/**
 * Modal för mass-tilldelning av fordon till en användare (dashboard-widgets).
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from '@/store/languageStore';
import { assignmentApi, AssignmentCategory } from '@/services/assignments/assignmentApi';
import { usersService, User } from '@/services/users/usersService';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { userMatchesSearch, userSelectLabel } from '@/utils/userSearch';

export interface DashboardAssignVehicleItem {
  vehicleId: number;
  registrationNumber: string;
  title: string;
}

interface DashboardVehicleAssignModalProps {
  items: DashboardAssignVehicleItem[];
  defaultCategoryCode: string;
  onClose: () => void;
  onAssigned: () => void;
}

export function DashboardVehicleAssignModal({
  items,
  defaultCategoryCode,
  onClose,
  onAssigned,
}: DashboardVehicleAssignModalProps) {
  const { t } = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [categories, setCategories] = useState<AssignmentCategory[]>([]);
  const [metaLoading, setMetaLoading] = useState(true);
  const [assignedTo, setAssignedTo] = useState('');
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [priority, setPriority] = useState('NORMAL');
  const [saving, setSaving] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMetaLoading(true);
    Promise.all([usersService.getUsers({ includeSubOrgs: true }), assignmentApi.getCategories()])
      .then(([u, c]) => {
        const sorted = [...u].sort((a, b) => {
          const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
          const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
          return nameA.localeCompare(nameB, 'sv');
        });
        setUsers(sorted);
        setCategories(c);
        const match = c.find(cat => cat.code === defaultCategoryCode);
        if (match) setCategoryId(match.id);
        else if (c.length > 0) setCategoryId(c[0].id);
        else setCategoryId('');
      })
      .catch(() => {
        toast.error(t('common.error'));
        setCategories([]);
        setCategoryId('');
      })
      .finally(() => setMetaLoading(false));
  }, [t, defaultCategoryCode]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setUserDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedUser = users.find(u => u.id === assignedTo);
  const filteredUsers = users.filter(u => {
    if (!userSearch) return true;
    return userMatchesSearch(u, userSearch);
  });

  const handleSelectUser = (userId: string) => {
    setAssignedTo(userId);
    setUserDropdownOpen(false);
    setUserSearch('');
  };

  const handleSubmit = async () => {
    if (!assignedTo || categoryId === '' || !Number.isFinite(Number(categoryId))) {
      toast.error(t('assignments.fillRequired'));
      return;
    }
    setSaving(true);
    try {
      const bulkItems = items.map(v => ({
        vehicle_id: v.vehicleId,
        title: v.title,
        registration_number: v.registrationNumber,
      }));
      const result = await assignmentApi.bulkCreate({
        items: bulkItems,
        category_id: categoryId as number,
        assigned_to: assignedTo,
        priority,
      });
      toast.success(`${result.created} ${t('assignments.assignmentsCreated')}`);
      onAssigned();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-bgCard rounded-xl shadow-lg p-6 w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-textDefault mb-1">{t('assignments.assignSelected')}</h2>
        <p className="text-sm text-textSecondary mb-4">
          {items.length} {t('assignments.vehiclesSelected')}:
          <span className="font-medium ml-1">{items.map(v => v.registrationNumber).join(', ')}</span>
        </p>

        <div className="space-y-3">
          <div ref={dropdownRef} className="relative">
            <label className="block text-sm font-medium text-textDefault mb-1">{t('assignments.assignedTo')} *</label>
            <div
              className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-bgDefault text-textDefault cursor-pointer flex items-center justify-between"
              onClick={() => setUserDropdownOpen(!userDropdownOpen)}
            >
              <span className={selectedUser ? 'text-textDefault' : 'text-textSecondary'}>
                {selectedUser ? `${selectedUser.first_name} ${selectedUser.last_name}` : t('assignments.selectUser')}
              </span>
              <ChevronDownIcon className="w-4 h-4 text-textSecondary" />
            </div>

            {userDropdownOpen && (
              <div className="absolute z-60 mt-1 w-full bg-bgCard border border-border rounded-lg shadow-xl max-h-64 flex flex-col">
                <div className="p-2 border-b border-border">
                  <input
                    type="text"
                    value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                    placeholder={t('common.search')}
                    className="w-full border border-border rounded px-2.5 py-1.5 text-sm bg-bgDefault text-textDefault focus:outline-none focus:ring-1 focus:ring-primary"
                    autoFocus
                  />
                </div>
                <div className="overflow-y-auto flex-1">
                  {filteredUsers.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-textSecondary">{t('common.noResults')}</div>
                  ) : (
                    filteredUsers.map(u => (
                      <div
                        key={u.id}
                        onClick={() => handleSelectUser(u.id)}
                        className={`px-3 py-2 text-sm cursor-pointer hover:bg-bgHover ${
                          u.id === assignedTo ? 'bg-primary/10 text-primary font-medium' : 'text-textDefault'
                        }`}
                      >
                        {userSelectLabel(u)}
                        <span className="text-xs text-textSecondary ml-1">({u.email})</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-textDefault mb-1">{t('assignments.category')}</label>
            <select
              value={categoryId === '' ? '' : String(categoryId)}
              onChange={e => {
                const v = e.target.value;
                setCategoryId(v === '' ? '' : parseInt(v, 10));
              }}
              disabled={metaLoading || categories.length === 0}
              className="w-full min-h-[2.5rem] border border-border rounded-lg px-3 py-2 text-sm bg-bgDefault text-textDefault disabled:opacity-60"
            >
              {metaLoading ? (
                <option value="" disabled>{t('common.loading')}</option>
              ) : categories.length === 0 ? (
                <option value="" disabled>{t('assignments.noCategories')}</option>
              ) : (
                categories.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))
              )}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-textDefault mb-1">{t('assignments.priorityLabel')}</label>
            <select
              value={priority}
              onChange={e => setPriority(e.target.value)}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-bgDefault text-textDefault"
            >
              <option value="LOW">{t('assignments.priorityLow')}</option>
              <option value="NORMAL">{t('assignments.priorityNormal')}</option>
              <option value="HIGH">{t('assignments.priorityHigh')}</option>
              <option value="URGENT">{t('assignments.priorityUrgent')}</option>
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-textSecondary hover:bg-bgHover rounded-lg">
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving || !assignedTo || categoryId === '' || metaLoading}
            className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
          >
            {saving ? t('common.saving') : t('assignments.assign')}
          </button>
        </div>
      </div>
    </div>
  );
}
